import React from 'react';
import { 
  Microscope, 
  Zap, 
  Target, 
  Shield,
  Search,
  Maximize,
  ArrowDownUp,
  Heart,
  CheckCircle2,
  Timer
} from 'lucide-react';
import './Dto43d.css';

const Dto43d: React.FC = () => {
  return (
    <div className="dto43d">
      {/* Hero Section */}
      <div className="hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>Microendodontic Treatment</h1>
          <p>Advanced Precision Root Canal Treatment Using Dental Microscope</p>
        </div>
      </div>

      {/* Introduction Section */}
      <section className="intro-section container">
        <div className="intro-content">
          <div className="intro-text">
            <h2>What is Microendodontic Treatment?</h2>
            <p>
              Microendodontic treatment represents a significant advancement in root canal 
              therapy, utilizing high-powered dental microscopes that provide 4-25x 
              magnification. This enhanced visualization, combined with specialized 
              instruments and ultrasonic technology, allows for more precise and 
              thorough treatment of infected root canals.
            </p>
          </div>
          <div className="intro-image">
            <img 
              src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&q=80&w=1500"
              alt="Dental Microscope"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Magnification Levels */}
      <section className="magnification-section">
        <div className="container">
          <h2 className="section-title">Precision Through Magnification</h2>
          <div className="magnification-grid">
            <div className="magnification-card">
              <Search className="mag-icon" />
              <h3>Low Magnification (2x-8x)</h3>
              <p>Provides comprehensive overview of the operating field</p>
            </div>
            
            <div className="magnification-card">
              <Maximize className="mag-icon" />
              <h3>Medium Magnification (8x-16x)</h3>
              <p>Optimal for main treatment procedures and surgical steps</p>
            </div>
            
            <div className="magnification-card">
              <ArrowDownUp className="mag-icon" />
              <h3>High Magnification (16x-25x)</h3>
              <p>Detailed examination of minute structures and documentation</p>
            </div>
          </div>
        </div>
      </section>

      {/* Clinical Applications */}
      <section className="applications-section">
        <div className="container">
          <h2 className="section-title">Clinical Applications</h2>
          <div className="applications-grid">
            <div className="application-card">
              <Target className="app-icon" />
              <h3>Minimally Invasive Access</h3>
              <p>
                Precise cavity opening with minimal tooth structure removal, preserving 
                natural strength and integrity.
              </p>
            </div>

            <div className="application-card">
              <Zap className="app-icon" />
              <h3>Root Canal Retreatment</h3>
              <p>
                Enhanced visualization for complete removal of old materials and 
                thorough cleaning using ultrasonic instruments.
              </p>
            </div>

            <div className="application-card">
              <Heart className="app-icon" />
              <h3>Vital Pulp Treatment</h3>
              <p>
                Careful management of deep decay near pulp tissue with precise 
                biomedical material application.
              </p>
            </div>

            <div className="application-card">
              <Search className="app-icon" />
              <h3>Crack Detection</h3>
              <p>
                Identification of microscopic cracks and fractures invisible to the 
                naked eye for early intervention.
              </p>
            </div>

            <div className="application-card">
              <Shield className="app-icon" />
              <h3>Microsurgical Procedures</h3>
              <p>
                Precise removal of infected tissue and accurate placement of root-end 
                filling materials.
              </p>
            </div>

            <div className="application-card">
              <Target className="app-icon" />
              <h3>Perforation Repair</h3>
              <p>
                Accurate location and repair of canal perforations with enhanced 
                visibility and precision.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section container">
        <h2 className="section-title">Key Advantages</h2>
        <div className="benefits-content">
          <div className="benefits-text">
            <ul className="benefits-list">
              <li>
                <CheckCircle2 className="benefit-icon" />
                <span>Enhanced visualization of complex root canal anatomy</span>
              </li>
              <li>
                <CheckCircle2 className="benefit-icon" />
                <span>More conservative tooth structure preservation</span>
              </li>
              <li>
                <CheckCircle2 className="benefit-icon" />
                <span>Higher success rates in complicated cases</span>
              </li>
              <li>
                <CheckCircle2 className="benefit-icon" />
                <span>Better identification of additional canals</span>
              </li>
              <li>
                <CheckCircle2 className="benefit-icon" />
                <span>Improved documentation and treatment planning</span>
              </li>
            </ul>
          </div>
          <div className="benefits-image">
            <img 
              src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&q=80&w=1500"
              alt="Dental Treatment"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Treatment Process */}
      <section className="process-section">
        <div className="container">
          <h2 className="section-title">Treatment Process</h2>
          <div className="process-grid">
            <div className="process-card">
              <Timer className="process-icon" />
              <h3>Initial Evaluation</h3>
              <p>
                Thorough examination using microscope to assess condition and plan 
                treatment approach.
              </p>
            </div>

            <div className="process-card">
              <Microscope className="process-icon" />
              <h3>Microscope-Assisted Treatment</h3>
              <p>
                Precise cleaning and shaping of root canals under high magnification.
              </p>
            </div>

            <div className="process-card">
              <Shield className="process-icon" />
              <h3>Advanced Disinfection</h3>
              <p>
                Thorough cleaning using ultrasonic technology and specialized 
                irrigation solutions.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dto43d;